export type SeenPayload = { id: number }

type VisibilityTarget = Element & { __rpa_payload?: SeenPayload }

export interface VisibilityObserver {
  observe(element: Element): void
  unobserve(element: Element): void
  disconnect(): void
  readonly observedSet: ReadonlySet<Element>
}
export function createVisibilityObserver({
  root,
  threshold = 0.6,
  rootMargin,
  onSeen,
}: {
  root: Element | null
  threshold?: number
  rootMargin?: string
  onSeen: (payload: SeenPayload) => void
}): VisibilityObserver {
  const seen = new WeakSet<Element>()
  const observed = new Set<Element>()

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < threshold) {
          return
        }

        if (seen.has(entry.target)) {
          return
        }

        seen.add(entry.target)

        const target = entry.target as VisibilityTarget
        const payload = target.__rpa_payload

        if (payload) {
          onSeen(payload)
        }
      })
    },
    { root, threshold, rootMargin },
  )

  return {
    observe(element) {
      observed.add(element)
      observer.observe(element)
    },
    unobserve(element) {
      const target = element as VisibilityTarget
      target.__rpa_payload = undefined
      observed.delete(element)
      observer.unobserve(element)
    },
    disconnect() {
      observer.disconnect()
      observed.clear()
    },
    observedSet: observed,
  }
}

export default createVisibilityObserver

export function isElementMostlyVisible(
  el: Element,
  root: Element | null,
  threshold = 0.75,
): boolean {
  const rect = el.getBoundingClientRect()
  const rootRect = root
    ? root.getBoundingClientRect()
    : {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        height: window.innerHeight,
        width: window.innerWidth,
      }

  const interTop = Math.max(rect.top, rootRect.top)
  const interBottom = Math.min(rect.bottom, rootRect.bottom)
  const visibleHeight = Math.max(0, interBottom - interTop)
  const ratio = visibleHeight / rect.height

  return ratio >= threshold
}
